import Button from '@/presentation/components/elements/Button';
import Table from '@/presentation/components/modules/Table';
import Link from 'next/link';
import { type Asset, type Collection, type VTokenBalance } from 'nftx.js';
import { useMemo, useState } from 'react';
import { QueryStatus } from '@tanstack/react-query';
import { useTranslations } from 'use-intl';
import Icon, { Icons } from '../../elements/Icon';
import EligibleVault from './EligibleVault';
import EligibleVaultToken from './EligibleVaultToken';

const EligibleVaults = ({
  vaultIds,
  collection,
  collectionAssets,
  slug,
  status,
  showHint,
}: {
  vaultIds: string[];
  collection?: Collection & { vTokens: VTokenBalance[] };
  collectionAssets?: Asset[];
  slug: string;
  status: QueryStatus;
  showHint?: boolean;
}) => {
  const t = useTranslations('Profile.Balances.EligibleVaults');
  const [dismissHint, setDismissHint] = useState(false);
  const mintableAssets =
    collectionAssets?.filter((a) =>
      a.vaultIds.some((x) => vaultIds.includes(x))
    ) || [];
  const hasMintableAssets = mintableAssets.length > 0;
  const mintableVaultIds = [
    ...new Set(mintableAssets.flatMap((asset) => asset.vaultIds)),
  ];

  const holdingsStatus = useMemo(() => {
    const vTokens = collection?.vTokens || [];
    if (hasMintableAssets && vTokens.length) {
      return 'HAS_NFTS_AND_TOKENS';
    }

    if (vTokens.length) {
      return 'HAS_ONLY_TOKENS';
    }

    if (hasMintableAssets) {
      return 'HAS_ONLY_NFTS';
    }

    if (!vaultIds.length) {
      return 'NO_VAULTS';
    }

    return 'NO_ASSETS';
  }, [hasMintableAssets, collection?.vTokens, vaultIds]);

  return (
    <Table
      header={
        <header className="flex justify-between items-center py-4 px-3 border-b border-gray-300 relative">
          <h3 className="font-bold text-sm">{t('title', { count: 1 })}</h3>
          {showHint && !dismissHint && (
            <div className="absolute top-4 right-2 bg-gray-50 text-sm rounded-md border border-gray-300 p-2 max-w-[30ch] shadow-xl text-left">
              <div className="relative">
                {
                  'Sell, swap, or add a position with your NFTs and tokens here. '
                }
                <button
                  type="button"
                  className="text-xs text-pink-500 font-medium underline hover:no-underline"
                  onClick={() => setDismissHint(true)}
                >
                  {'Okay'}
                </button>
                {'.'}
                <span className="absolute top-full right-2 w-0 h-0 border-t-gray-50 border-l-transparent border-r-transparent border-l-[16px] border-r-[16px] border-t-[16px]" />
              </div>
            </div>
          )}
        </header>
      }
      headings={[
        {
          label: t('table.nfts'),
          align: 'left',
        },
        {
          label: t('table.items'),
        },
        {
          label: t('table.price'),
        },
        {
          label: t('table.apr'),
        },
        {
          label: t('table.volume'),
        },
        {
          label: t('table.action'),
        },
      ]}
    >
      {status === 'loading' && (
        <tr>
          <td colSpan={6} className="p-4 text-center">
            <Icon
              name={Icons.SPINNER_GAP}
              className="animate-spin inline-flex"
              size={24}
            />
          </td>
        </tr>
      )}
      {status === 'success' && (
        <>
          {hasMintableAssets ? (
            vaultIds.map(
              (vaultId) =>
                mintableVaultIds.includes(vaultId) && (
                  <EligibleVault
                    key={vaultId}
                    vaultId={vaultId}
                    collectionAssets={collectionAssets}
                  />
                )
            )
          ) : (
            <tr>
              <td colSpan={6} className="p-4 text-center">
                <p>{t('noEligibleAssets')}</p>
              </td>
            </tr>
          )}

          {(holdingsStatus === 'HAS_ONLY_TOKENS' ||
            holdingsStatus === 'HAS_NFTS_AND_TOKENS') && (
            <tr>
              {[
                {
                  label: t('table.tokens'),
                  className: 'bg-gray-100 text-left',
                },
                {
                  label: t('table.balance'),
                  className: 'bg-gray-100 text-right',
                },
                {
                  label: t('table.price'),
                  className: 'bg-gray-100 text-right',
                },
                {
                  label: t('table.apr'),
                  className: 'bg-gray-100 text-right',
                },
                {
                  label: t('table.volume'),
                  className: 'bg-gray-100 text-right',
                },
                {
                  label: t('table.action'),
                  className: 'bg-gray-100 text-right',
                },
              ].map((x) => (
                <th
                  key={x.label}
                  className={`${x.className} px-3 py-2 uppercase text-xs`}
                >
                  {x.label}
                </th>
              ))}
            </tr>
          )}
          {collection?.vTokens.map((v) => (
            <EligibleVaultToken {...v} collection={collection} key={v.id} />
          ))}

          {holdingsStatus === 'NO_VAULTS' && (
            <tr>
              <td colSpan={6} className="p-4 text-center">
                <p className="mb-4">{t('noVaults')}</p>
                <Link
                  href={`/${slug}/create?collectionAddress=${collection?.address}`}
                  passHref={true}
                  legacyBehavior={true}
                >
                  <Button
                    variant="primary"
                    className="inline-flex"
                    size="small"
                  >
                    {t('create')}
                  </Button>
                </Link>
              </td>
            </tr>
          )}
        </>
      )}
    </Table>
  );
};

export default EligibleVaults;
